/**
 * shared/multiLocation.ts
 *
 * Multi-Location Support — Session 5 (Control Center wiring).
 *
 * Display-only mirror of backend `agents/multiLocation/locationHelpers.ts`.
 * Provides the read-side helpers React UIs need to render multi-location
 * data without re-importing the backend module (which has Firestore
 * dependencies the React app shouldn't pull in).
 *
 * Mirrors the canonical backend invariants:
 *   - effectiveLocationCount always returns ≥ 1 (billing safety)
 *   - listActiveLocations filters by `active !== false`
 *   - isMultiLocation gates the LocationSwitcher dropdown (≥2 active)
 *   - selectLocationsForDisplay synthesizes a single "default" entry for
 *     pre-multi-location clients so iteration loops work identically
 *
 * Pure module — no React, no Firestore, no I/O. Tested via shared tests
 * under app/shared/src/__tests__/.
 *
 * Mutations (add / remove / deactivate locations) go through the backend
 * via the onboarding flow (S3a) or Ava intents — the UI never mutates
 * locations[] directly. Post-mutation, the Cloud Function calls
 * syncGoogleLocationQuantity (S4) to re-sync the Stripe quantity.
 */

import type { ClientDocument, ClientLocation } from './hooks/useClientAuth'

// ─── Constants ──────────────────────────────────────────────────────────────

/**
 * Minimum number of active locations required to show the LocationSwitcher
 * dropdown in the Control Center header. Matches the backend constant
 * `MULTI_LOCATION_UI_THRESHOLD` exactly.
 */
export const MULTI_LOCATION_UI_THRESHOLD = 2

// ─── Read-side helpers (display only) ───────────────────────────────────────

/**
 * Pure: active subset of a client's locations (filter by `active !== false`).
 *
 * Returns [] when client has no `locations` field (pre-multi-location schema)
 * or when every location is deactivated. Callers wanting the LocationSwitcher
 * to render in either case should compose with the synthetic-fallback path
 * via `selectLocationsForDisplay`.
 */
export function listActiveLocations(
  client: Pick<ClientDocument, 'locations'> | null | undefined
): ClientLocation[] {
  const all = client?.locations ?? []
  return all.filter((l) => l.active !== false)
}

/**
 * Pure: effective count of locations for billing + iteration. Always ≥1.
 *
 * Mirrors the backend `effectiveLocationCount` invariant exactly — the UI
 * uses it to render per-location agent counts (e.g., "Google Agent ×3 =
 * $225/mo") that match what the client is actually billed.
 */
export function effectiveLocationCount(
  client: Pick<ClientDocument, 'locations'> | null | undefined
): number {
  const active = listActiveLocations(client)
  return Math.max(1, active.length)
}

/**
 * Pure: is this client genuinely operating in multi-location mode?
 *
 * True iff `locations[]` has ≥2 active entries — drives the LocationSwitcher
 * decision to render (the dropdown is hidden for single-loc clients to keep
 * the header clean).
 */
export function isMultiLocation(
  client: Pick<ClientDocument, 'locations'> | null | undefined
): boolean {
  return listActiveLocations(client).length >= MULTI_LOCATION_UI_THRESHOLD
}

// ─── Display-time fallback (single-loc → synthetic LocationSwitcher entry) ──

/**
 * The shape the LocationSwitcher (and per-location detail views) consume.
 * Includes the location's display fields PLUS the GBP rating/review summary
 * the dropdown surfaces to make location selection meaningful at-a-glance.
 *
 * Synthetic entries (id='default') come from the top-level ClientDocument
 * gbp* fields for clients without a `locations[]` array. This makes UI loops
 * work identically for single-loc and multi-loc clients — same display
 * primitive, same iteration loop, different underlying data.
 */
export interface DisplayLocation {
  id: string
  name: string
  address: string
  gbpConnected: boolean
  rating?: number
  reviewCount?: number
}

/**
 * Pure: format a ClientLocationAddress into a single-line display string.
 *
 *   { street1: '123 Main St', city: 'Boise', state: 'ID', postalCode: '83702' }
 *   → '123 Main St, Boise, ID 83702'
 *
 * Falls back to street1 only when city/state are missing. Drops empty parts
 * gracefully — never renders trailing commas or "undefined" strings.
 */
export function formatLocationAddress(
  address: ClientLocation['address']
): string {
  if (!address) return ''
  const street = address.street2
    ? `${address.street1}, ${address.street2}`
    : address.street1
  const cityStateZip = [
    address.city,
    [address.state, address.postalCode].filter(Boolean).join(' ').trim(),
  ]
    .filter(Boolean)
    .join(', ')
  return [street, cityStateZip].filter(Boolean).join(', ')
}

/**
 * Pure: return DisplayLocation entries for a client.
 *
 * Multi-loc client (has 1+ active locations[]):
 *   → one DisplayLocation per active entry, using per-location GBP fields.
 *
 * Single-loc client (no locations[] OR all inactive):
 *   → ONE synthetic entry id='default', name=businessName, with GBP fields
 *     copied from the top-level ClientDocument. Preserves backward compat
 *     for every pre-multi-location client without forcing schema migration.
 *
 * The LocationSwitcher gates its own visibility (renders nothing when
 * length ≤ 1), so a single-loc client gets the synthetic entry but the
 * dropdown stays hidden. Per-location detail views can still iterate the
 * same array — they just show one card.
 */
export function selectLocationsForDisplay(
  client:
    | (Pick<ClientDocument, 'locations' | 'businessName' | 'address' | 'gbpPlaceId' | 'gbpProfileUrl' | 'gbpAccessGranted' | 'gbpRating' | 'gbpReviewCount'> | null
    | undefined)
): DisplayLocation[] {
  const active = listActiveLocations(client ?? null)
  if (active.length > 0) {
    return active.map((l) => ({
      id: l.id,
      name: l.name,
      address: formatLocationAddress(l.address),
      gbpConnected: l.gbpAccessGranted === true,
      ...(typeof l.gbpRating === 'number' ? { rating: l.gbpRating } : {}),
      ...(typeof l.gbpReviewCount === 'number' ? { reviewCount: l.gbpReviewCount } : {}),
    }))
  }
  // Synthetic single-loc fallback.
  if (!client) return []
  return [
    {
      id: 'default',
      name: client.businessName ?? 'Primary Location',
      address: client.address ?? '',
      gbpConnected: client.gbpAccessGranted === true,
      ...(typeof client.gbpRating === 'number' ? { rating: client.gbpRating } : {}),
      ...(typeof client.gbpReviewCount === 'number' ? { reviewCount: client.gbpReviewCount } : {}),
    },
  ]
}
